import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;
const MAX_FILE_SIZE_MB = 50;
const MAX_FILES = 10;

// Validation functions
const validateFileCount = (currentCount, newCount) => {
  if (currentCount + newCount > MAX_FILES) {
    toast.error(`Maximum ${MAX_FILES} files allowed per print job`);
    return false;
  }
  return true;
};

const validateFileSize = (files) => {
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE_MB * BYTES_PER_MB) {
      toast.error(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return false;
    }
  }
  return true;
};

// Upload single file
const uploadSingleFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  
  return {
    ...response.data,
    id: Date.now() + Math.random()
  };
};

// File list item component
const FileListItem = ({ file, onRemove }) => (
  <div
    className="flex items-center gap-3 p-3 bg-secondary rounded-lg"
    data-testid={`uploaded-file-${file.id}`}
  >
    <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm truncate">{file.filename}</p>
      <p className="text-xs text-muted-foreground">
        {file.pages} page{file.pages !== 1 ? 's' : ''} • {(file.size / BYTES_PER_MB).toFixed(2)} MB
      </p>
    </div>
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-destructive"
      onClick={() => onRemove(file.id)}
      data-testid={`remove-file-${file.id}`}
    >
      <X className="w-4 h-4" />
    </Button>
  </div>
);

// Upload summary component
const UploadSummary = ({ fileCount, totalPages, totalSize }) => (
  <div className="bg-accent/10 rounded-lg p-3 mt-3">
    <div className="flex justify-between items-center">
      <span className="text-sm font-medium">Total:</span>
      <span className="text-sm">
        {fileCount} file{fileCount !== 1 ? 's' : ''}, {totalPages} pages, {(totalSize / BYTES_PER_MB).toFixed(2)} MB
      </span>
    </div>
  </div>
);

const FileUploadStep = ({ onFilesUploaded, loading, setLoading }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (!validateFileCount(uploadedFiles.length, files.length)) return;
    if (!validateFileSize(files)) return;

    setIsUploading(true);
    setLoading(true);

    try {
      const newFiles = [];
      for (const file of files) {
        const uploadedFile = await uploadSingleFile(file);
        newFiles.push(uploadedFile);
      }

      const allFiles = [...uploadedFiles, ...newFiles];
      setUploadedFiles(allFiles);
      
      const totalPages = allFiles.reduce((sum, f) => sum + f.pages, 0);
      toast.success(`${files.length} file(s) uploaded: ${totalPages} total pages`);
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Failed to upload file(s)';
      toast.error(message);
    } finally {
      setIsUploading(false);
      setLoading(false);
      e.target.value = '';
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleContinue = () => {
    if (uploadedFiles.length === 0) {
      toast.error('Please upload at least one file');
      return;
    }
    onFilesUploaded(uploadedFiles);
  };

  const totalPages = uploadedFiles.reduce((sum, f) => sum + f.pages, 0);
  const totalSize = uploadedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <Card className="w-full max-w-md mx-4" data-testid="upload-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-xl sm:text-2xl">Upload Documents</CardTitle>
        <CardDescription className="text-sm">
          Select one or more files to print (max {MAX_FILES} files, {MAX_FILE_SIZE_MB}MB each)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {/* Upload area - entire box is clickable */}
        <label 
          htmlFor="file-upload" 
          className={`block border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-colors ${
            isUploading 
              ? 'border-accent bg-accent/5 cursor-wait' 
              : 'border-border hover:border-accent cursor-pointer'
          }`}
          data-testid="upload-area"
        >
          {isUploading ? (
            /* Upload in progress state */
            <div className="py-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 border-4 border-accent/20 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
              </div>
              <div className="space-y-2">
                <p className="text-accent font-semibold text-base sm:text-lg">Uploading your files...</p>
                <p className="text-muted-foreground text-sm">Please wait, this may take a moment</p>
                <div className="flex items-center justify-center gap-1 mt-3">
                  <span className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                  <span className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                  <span className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                </div>
              </div>
            </div>
          ) : (
            /* Default upload state */
            <>
              <Upload className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-muted-foreground" />
              <div>
                <span className="text-accent font-medium text-sm sm:text-base">Click to upload</span>
                <span className="text-muted-foreground text-sm sm:text-base"> or drag and drop</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                PDF, DOC, DOCX, TXT (Max {MAX_FILE_SIZE_MB}MB each)
              </p>
            </>
          )}
          <Input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.txt"
            multiple
            disabled={isUploading}
            data-testid="file-input"
          />
        </label>

        {/* Uploaded files list */}
        {uploadedFiles.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{uploadedFiles.length} file(s) uploaded</span>
              <span>{totalPages} total pages</span>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {uploadedFiles.map((file) => (
                <FileListItem key={file.id} file={file} onRemove={removeFile} />
              ))}
            </div>

            {/* Add more files button */}
            {uploadedFiles.length < MAX_FILES && (
              <label 
                htmlFor="file-upload" 
                className="flex items-center justify-center gap-2 p-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-secondary transition-colors"
              >
                <Plus className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Add more files</span>
              </label>
            )}

            <UploadSummary 
              fileCount={uploadedFiles.length} 
              totalPages={totalPages} 
              totalSize={totalSize} 
            />
          </div>
        )}

        {/* Continue button */}
        {uploadedFiles.length > 0 && (
          <Button
            className="w-full btn-primary"
            onClick={handleContinue}
            disabled={loading || isUploading}
            data-testid="continue-to-details-btn"
          >
            {isUploading ? 'Uploading...' : 'Continue to Print Settings'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default FileUploadStep;
